import { VIEWER_TERMS_HTML } from './terms_viewer_content';

export const STORAGE_VIEWER_TC_ACCEPTED = 'fc_viewer_tc_accepted';

let activeViewerTermsPrompt: Promise<void> | null = null;

/**
 * Checks whether the viewer has accepted any Terms and Conditions in localStorage or cookies.
 * As long as any acceptance is present, viewers are not prompted again.
 */
export function hasViewerAcceptedTC(): boolean {
  const val = localStorage.getItem(STORAGE_VIEWER_TC_ACCEPTED);
  if (val && val.trim().length > 0) {
    return true;
  }

  if (typeof document !== 'undefined' && document.cookie) {
    const cookies = document.cookie.split(';');
    for (const c of cookies) {
      const trimmed = c.trim();
      if (trimmed.startsWith(STORAGE_VIEWER_TC_ACCEPTED + '=')) {
        const cookieVal = trimmed.substring(STORAGE_VIEWER_TC_ACCEPTED.length + 1);
        if (cookieVal.trim().length > 0) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Ensures that the viewer has accepted Terms and Conditions.
 * If not already accepted, displays a gate prompt and only continues once accepted.
 */
export async function ensureViewerTermsAccepted(): Promise<void> {
  if (hasViewerAcceptedTC()) {
    return;
  }

  if (activeViewerTermsPrompt) {
    return activeViewerTermsPrompt;
  }

  activeViewerTermsPrompt = doEnsureViewerTermsAccepted().finally(() => {
    activeViewerTermsPrompt = null;
  });

  return activeViewerTermsPrompt;
}

async function doEnsureViewerTermsAccepted(): Promise<void> {
  const gate = document.getElementById('viewer-tc-gate');
  const checkbox = document.getElementById('viewer-tc-checkbox') as HTMLInputElement | null;
  const continueBtn = document.getElementById('viewer-tc-continue-btn') as HTMLButtonElement | null;
  const linkBtn = document.getElementById('viewer-tc-link-btn') as HTMLButtonElement | null;
  const modal = document.getElementById('viewer-tc-modal');
  const modalContent = document.getElementById('viewer-tc-modal-content');
  const modalCloseBtn = document.getElementById('viewer-tc-modal-close-btn');
  const modalDismissBtn = document.getElementById('viewer-tc-modal-dismiss-btn');

  if (!gate || !checkbox || !continueBtn) {
    return;
  }

  checkbox.checked = false;
  continueBtn.disabled = true;
  gate.classList.remove('hidden');

  const loadTermsContent = () => {
    if (!modalContent) return;
    modalContent.innerHTML = VIEWER_TERMS_HTML;
  };

  if (linkBtn && modal) {
    linkBtn.onclick = (e) => {
      e.preventDefault();
      loadTermsContent();
      modal.classList.remove('hidden');
    };
  }

  const closeModal = () => {
    if (modal) modal.classList.add('hidden');
  };

  if (modalCloseBtn) modalCloseBtn.onclick = closeModal;
  if (modalDismissBtn) modalDismissBtn.onclick = closeModal;

  if (modal) {
    modal.onclick = (e: MouseEvent) => {
      if (e.target === modal) {
        closeModal();
      }
    };
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && modal && !modal.classList.contains('hidden')) {
      closeModal();
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', onKeyDown);
  }

  return new Promise<void>((resolve) => {
    checkbox.onchange = () => {
      continueBtn.disabled = !checkbox.checked;
    };

    continueBtn.onclick = () => {
      localStorage.setItem(STORAGE_VIEWER_TC_ACCEPTED, 'true');
      if (typeof document !== 'undefined') {
        document.cookie = `${STORAGE_VIEWER_TC_ACCEPTED}=true; path=/; max-age=31536000; SameSite=Lax`;
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('keydown', onKeyDown);
      }
      gate.classList.add('hidden');
      closeModal();
      resolve();
    };
  });
}
